import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { usePerspective } from "@/context/PerspectiveContext";
import { getContent } from "@/content/perspectives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "AI Strategy & Roadmapping",
  "Intelligent Process Automation",
  "Data & Decision Intelligence",
  "Digital Transformation",
  "Not sure yet",
];

const FIELD =
  "w-full border-0 border-b border-ink/20 bg-transparent px-0 py-4 font-light text-ink placeholder:text-ink/35 focus:border-cobalt focus:outline-none focus:ring-0 transition-colors";

const Contact = () => {
  const { perspective } = usePerspective();
  const { contact } = getContent(perspective);
  const [form, setForm] = useState({ name: "", email: "", company: "", interest: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please share your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSent(true);
      toast.success("Thank you — your enquiry has reached us.");
      setForm({ name: "", email: "", company: "", interest: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-white px-6 py-28 lg:px-12 lg:py-40">
      <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-24">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-cobalt">
              {contact.overline}
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-serif text-5xl font-medium leading-[1] tracking-tight text-ink lg:text-7xl">
              {contact.heading}<br /><span className="italic text-cobalt">{contact.headingItalic}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-md text-base font-light leading-relaxed text-ink/60">
              {contact.body}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-12 space-y-4 border-t border-black/10 pt-8">
              <div className="font-mono text-xs uppercase tracking-[0.15em] text-ink/40">
                General enquiries
              </div>
              <a
                href="mailto:hello@prospera.ai"
                data-testid="contact-email-link"
                className="block font-serif text-2xl text-ink transition-colors hover:text-cobalt"
              >
                hello@prospera.ai
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            {sent ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex h-full min-h-[400px] flex-col justify-center border border-cobalt/20 bg-[#f5f7fb] p-12"
                data-testid="contact-success"
              >
                <div className="font-serif text-4xl font-medium text-ink">Received.</div>
                <p className="mt-4 max-w-sm text-base font-light text-ink/60">
                  Your enquiry is with our team. Expect a considered reply shortly.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-8 w-fit border-b border-cobalt pb-1 font-mono text-xs uppercase tracking-[0.15em] text-cobalt"
                  data-testid="contact-reset"
                >
                  Send another →
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.15em] text-ink/40">Full name</label>
                  <input data-testid="input-name" className={FIELD} value={form.name} onChange={update("name")} placeholder="Jane Doe" />
                </div>
                <div>
                  <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.15em] text-ink/40">Work email</label>
                  <input data-testid="input-email" type="email" className={FIELD} value={form.email} onChange={update("email")} placeholder="jane@company.com" />
                </div>
                <div>
                  <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.15em] text-ink/40">Company</label>
                  <input data-testid="input-company" className={FIELD} value={form.company} onChange={update("company")} placeholder="Company Inc." />
                </div>
                <div>
                  <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.15em] text-ink/40">Interested in</label>
                  <select data-testid="input-interest" className={`${FIELD} appearance-none`} value={form.interest} onChange={update("interest")}>
                    <option value="">Select a discipline</option>
                    {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.15em] text-ink/40">How can we help?</label>
                  <textarea data-testid="input-message" rows={4} className={`${FIELD} resize-none`} value={form.message} onChange={update("message")} placeholder="Tell us about the challenge you're facing…" />
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="group flex w-full items-center justify-center gap-3 bg-cobalt py-5 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-cobalt-dark disabled:opacity-60 sm:w-auto sm:px-16"
                  >
                    {loading ? "Sending…" : "Submit enquiry"}
                    {!loading && <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>}
                  </button>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
